import { AppSettings } from '../interfaces/app-settings.interface';
import { BoardConfig } from '../interfaces/board-config.interface';
import { IndicatorWidgetConfig } from '../interfaces/indicator-widget-config.interface';
import { DashboardFilterType } from '../types/dashboard-filter.type';
import { StatusboardFilterType } from '../types/statusboard-filter.type';

export function collectVehicleSerials(settings: AppSettings): string[] {
	const serials = new Set<string>(settings.selectedMachines ?? []);

	if (settings.machineToShow) {
		serials.add(settings.machineToShow);
	}

	collectWidgetSerials(settings.indicatorWidgets, serials);

	(settings.boards ?? []).forEach((board) => {
		collectWidgetSerials(board.indicatorWidgets, serials);
		collectFilterSerials(board.statusboardFilter?.vehicles, serials);
		collectFilterSerials(board.dashboardFilter?.vehicles, serials);
	});

	return Array.from(serials);
}

export function pruneInaccessibleVehicles(
	settings: AppSettings,
	accessibleSerials: Set<string>
): AppSettings | null {
	const selectedMachines = (settings.selectedMachines ?? []).filter((serial) =>
		accessibleSerials.has(serial)
	);

	const prunedSettings: AppSettings = {
		...settings,
		selectedMachines,
		machineToShow: accessibleSerials.has(settings.machineToShow)
			? settings.machineToShow
			: (selectedMachines[0] ?? ''),
		indicatorWidgets: pruneWidgets(settings.indicatorWidgets, accessibleSerials),
		boards: pruneBoards(settings.boards, accessibleSerials),
	};

	return getVehicleSignature(settings) === getVehicleSignature(prunedSettings)
		? null
		: prunedSettings;
}

function collectWidgetSerials(
	widgets: IndicatorWidgetConfig[] | undefined,
	serials: Set<string>
): void {
	(widgets ?? []).forEach((widget) => collectFilterSerials(widget.vehicles, serials));
}

function collectFilterSerials(
	vehicles: { serial: string }[] | undefined,
	serials: Set<string>
): void {
	(vehicles ?? []).forEach((vehicle) => {
		if (vehicle?.serial) {
			serials.add(vehicle.serial);
		}
	});
}

function pruneWidgets(
	widgets: IndicatorWidgetConfig[] | undefined,
	accessibleSerials: Set<string>
): IndicatorWidgetConfig[] {
	return (widgets ?? []).map((widget) => ({
		...widget,
		vehicles: (widget.vehicles ?? []).filter((vehicle) => accessibleSerials.has(vehicle.serial)),
	}));
}

function pruneBoards(
	boards: BoardConfig[] | undefined,
	accessibleSerials: Set<string>
): BoardConfig[] {
	return (boards ?? []).map((board) => {
		const prunedBoard: BoardConfig = { ...board };

		if (board.indicatorWidgets) {
			prunedBoard.indicatorWidgets = pruneWidgets(board.indicatorWidgets, accessibleSerials);
		}
		if (board.statusboardFilter) {
			prunedBoard.statusboardFilter = pruneStatusboardFilter(
				board.statusboardFilter,
				accessibleSerials
			);
		}
		if (board.dashboardFilter) {
			prunedBoard.dashboardFilter = pruneDashboardFilter(board.dashboardFilter, accessibleSerials);
		}

		return prunedBoard;
	});
}

function pruneStatusboardFilter(
	filter: StatusboardFilterType,
	accessibleSerials: Set<string>
): StatusboardFilterType {
	const vehicles = (filter.vehicles ?? []).filter((vehicle) =>
		accessibleSerials.has(vehicle.serial)
	);

	return { ...filter, vehicles, isActive: vehicles.length > 0 && filter.isActive };
}

function pruneDashboardFilter(
	filter: DashboardFilterType,
	accessibleSerials: Set<string>
): DashboardFilterType {
	return {
		...filter,
		vehicles: (filter.vehicles ?? []).filter((vehicle) => accessibleSerials.has(vehicle.serial)),
	};
}

function getVehicleSignature(settings: AppSettings): string {
	return JSON.stringify([
		settings.selectedMachines ?? [],
		settings.machineToShow ?? '',
		getWidgetSignature(settings.indicatorWidgets),
		(settings.boards ?? []).map((board) => [
			getWidgetSignature(board.indicatorWidgets),
			board.statusboardFilter?.isActive ?? false,
			getSerials(board.statusboardFilter?.vehicles),
			getSerials(board.dashboardFilter?.vehicles),
		]),
	]);
}

function getWidgetSignature(widgets: IndicatorWidgetConfig[] | undefined): string[][] {
	return (widgets ?? []).map((widget) => getSerials(widget.vehicles));
}

function getSerials(vehicles: { serial: string }[] | undefined): string[] {
	return (vehicles ?? []).map((vehicle) => vehicle.serial);
}
